// src/pages/Register.tsx
import { CalendarClock, GraduationCap, IdCard, Mail, Phone, User } from 'lucide-react'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { INSTITUTION, isKnownSchool, isValidInstitution, RULES, SCHOOLS } from '@sdg/shared'
import {
  Card,
  CardContent,
  CardDescription,
  CardEyebrow,
  CardHeader,
  CardTitle,
  Note,
} from '../components/ui/card'
import { Button } from '../components/ui/button'
import { Field, InputWithIcon } from '../components/ui/input'
import { Select } from '../components/ui/select'
import { ApiError, post, rateLimitMessage } from '../lib/api'
import { cn } from '../lib/cn'
import { formatDuration, formatMoment } from '../lib/countdown'
import {
  GateClosed,
  GateLoading,
  GateOpensSoon,
  isWindowRefusal,
  useRegistrationGate,
} from '../components/RegistrationGate'
import { savePending } from '../lib/session'
import AuthLayout from '../components/AuthLayout'

type RegisterResponse = {
  status: 'otp_sent'
  maskedEmail: string
  resendInSeconds: number
}

interface Form {
  /**
   * The first question, and the one the rest of the form depends on.
   *
   * Null until answered, not `false`: defaulting it would silently decide for
   * the student which half of the form they get, and most of them would never
   * notice the question was there.
   */
  isLincolnStudent: boolean | null
  name: string
  /** The chosen Lincoln school, or the institution typed by a visitor. */
  school: string
  studentId: string
  email: string
  phone: string
  pdpaConsent: boolean
}

const EMPTY: Form = {
  isLincolnStudent: null,
  name: '',
  school: '',
  studentId: '',
  email: '',
  phone: '',
  pdpaConsent: false,
}

/**
 * Mirrors the server rules (slide 8) so mistakes surface before a round trip.
 *
 * The school and Student ID rules differ by answer, which is the whole point
 * of the first question: a visitor from another college has no Lincoln
 * Student ID and belongs to none of the 14 schools, and asking them for
 * either is asking them to invent one.
 */
function validate(form: Form): Partial<Record<keyof Form, string>> {
  const errors: Partial<Record<keyof Form, string>> = {}
  const lincoln = form.isLincolnStudent === true

  if (form.isLincolnStudent === null) {
    errors.isLincolnStudent = 'Please answer this first.'
  }
  if (form.name.trim().length < 2) errors.name = 'Enter your full name.'

  if (lincoln) {
    if (!isKnownSchool(form.school)) errors.school = 'Select your school.'
    if (form.studentId.trim().length < 3) errors.studentId = 'Enter your Student ID.'
  } else if (form.isLincolnStudent === false && !isValidInstitution(form.school)) {
    // Separate messages: "too long" is a different problem from "missing",
    // and `maxLength` on the input means this can only be reached by a paste
    // the browser truncated — so say what the limit is.
    errors.school =
      form.school.trim().length > INSTITUTION.maxLength
        ? `Please shorten this to ${INSTITUTION.maxLength} characters or fewer.`
        : 'Enter the name of your school, college or university.'
  }

  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) errors.email = 'Enter a valid e-mail.'
  if (!/^\+?[\d\s-]{7,20}$/.test(form.phone.trim())) errors.phone = 'Enter a valid phone number.'
  if (!form.pdpaConsent) errors.pdpaConsent = 'Please accept the PDPA notice to continue.'

  return errors
}

// Shared compact styling (same look as Login)
const inputClass =
  'h-11 rounded-lg border-slate-200 bg-white text-sm focus:border-blue-500 focus:ring-blue-500/20'

export default function Register() {
  const navigate = useNavigate()
  const [form, setForm] = useState<Form>(EMPTY)
  const [errors, setErrors] = useState<Partial<Record<keyof Form, string>>>({})
  const [submitting, setSubmitting] = useState(false)
  const [serverError, setServerError] = useState<string | null>(null)
  /** A rate-limit refusal, already worded for a student. */
  const [rateLimited, setRateLimited] = useState<string | null>(null)

  /**
   * The registration window Admin configured — the same gate the sign-in page
   * uses. In development mode it always reports open.
   */
  const gate = useRegistrationGate()

  const set = <K extends keyof Form>(key: K, value: Form[K]) => {
    setForm((f) => ({ ...f, [key]: value }))
    setErrors((e) => ({ ...e, [key]: undefined }))
    setServerError(null)
    setRateLimited(null)
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    const found = validate(form)
    setErrors(found)
    if (Object.keys(found).length > 0) return

    setSubmitting(true)
    setServerError(null)
    setRateLimited(null)

    const isLincolnStudent = form.isLincolnStudent === true

    try {
      const res = await post<RegisterResponse>('/api/student/register', {
        isLincolnStudent,
        name: form.name.trim(),
        school: form.school.trim(),
        /*
         * Omitted entirely for a visitor, rather than sent as ''. They have
         * no Lincoln ID, and a student who typed one, then changed their
         * answer to "No", must not have that stale value travel with the
         * form — the server drops it too, but the request should not carry
         * an identity the sender does not hold.
         */
        ...(isLincolnStudent ? { studentId: form.studentId.trim().toUpperCase() } : {}),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim(),
        pdpaConsent: form.pdpaConsent,
      })

      savePending({
        email: form.email.trim().toLowerCase(),
        maskedEmail: res.maskedEmail,
        resendInSeconds: res.resendInSeconds,
      })
      navigate('/verify')
    } catch (err) {
      /*
       * Registering sends an OTP, so it is the route most likely to be rate
       * limited — and the one where a bare failure is most misleading: the
       * student has done nothing wrong, they are just early.
       */
      const limit = rateLimitMessage(err)
      if (limit) setRateLimited(limit)
      else setServerError((err as ApiError).message)

      /*
       * The server says we are outside the window and the page thought we
       * were inside it — a form left open in a tab since yesterday, or a
       * device clock that is minutes out. Re-read the window so the card
       * turns over into the countdown or the closed notice, instead of
       * leaving a form that will be refused however many times it is sent.
       */
      if (isWindowRefusal((err as ApiError).message)) gate.refetch()
    } finally {
      setSubmitting(false)
    }
  }

  /*
   * Held back until the window is known.
   *
   * Rendering the form first and replacing it a moment later would be worse
   * than a brief wait: a student who starts typing into a form that is about
   * to be swapped for a countdown loses what they typed and reasonably
   * concludes the site is broken.
   *
   * A *failed* status call does not hold anything back, though — the form is
   * shown and the server still enforces the window. Refusing to show the
   * registration page because one status request timed out on venue Wi-Fi
   * would turn a flaky network into a closed registration.
   */
  if (gate.status === 'loading') {
    return (
      <AuthLayout>
        <GateLoading />
      </AuthLayout>
    )
  }

  if (gate.status === 'before') {
    return (
      <AuthLayout>
        <GateOpensSoon
          audience="register"
          opensAt={gate.window?.opensAt ?? null}
          seconds={gate.untilOpen ?? 0}
        />
      </AuthLayout>
    )
  }

  if (gate.status === 'closed') {
    return (
      <AuthLayout>
        <GateClosed closedAt={gate.window?.closesAt ?? null} />
      </AuthLayout>
    )
  }

  return (
    <AuthLayout>
      <Card className="w-full max-w-md rounded-2xl border border-white bg-white/90 text-slate-900 shadow-xl shadow-blue-900/10">
        <CardHeader className="space-y-1 px-6 pb-2 pt-6">
          <CardEyebrow className="text-[11px] font-bold uppercase tracking-widest text-orange-600">
            Student Registration
          </CardEyebrow>
          <CardTitle className="text-2xl font-bold text-[#0B1E3F]">Join the celebration!</CardTitle>
          <CardDescription className="text-sm text-slate-500">
            Visit {RULES.luckyDrawThreshold} booths to enter the lucky draw, and{' '}
            {RULES.certificateThreshold} for your e-certificate.
          </CardDescription>
        </CardHeader>

        <CardContent className="px-6 pb-6 pt-3">
          {/*
            Registration closes today. Said plainly at the top of the form,
            because the most expensive moment to find this out is after
            filling it in.

            Gated on `enforced`: in development mode a closing time may be
            configured but is not applied, and warning a student that
            registration closes in two hours when it does not would be a lie
            told by the dry run.
          */}
          {gate.enforced && gate.untilClose !== null && gate.untilClose > 0 && gate.untilClose <= 86_400 && (
            <Note
              tone="amber"
              className="mb-3.5 rounded-lg border-amber-200 bg-amber-50/80 text-amber-800"
            >
              <span className="flex items-start gap-2.5">
                <CalendarClock className="mt-0.5 size-4 shrink-0" strokeWidth={2.2} />
                <span>
                  Registration closes {formatMoment(gate.window?.closesAt ?? null)} — in{' '}
                  <b className="font-semibold">{formatDuration(gate.untilClose)}</b>.
                </span>
              </span>
            </Note>
          )}

          <form onSubmit={onSubmit} noValidate className="space-y-3.5">
            {/*
              The first question, and the one that shapes the rest of the
              form. Asked before anything else because the answer decides
              whether there is a Student ID to give at all — asking it last
              would mean a visitor had already been shown, and possibly
              filled in, a field that does not apply to them.

              Two buttons rather than a drop-down or a checkbox: there is no
              sensible default to pre-select, and `isLincolnStudent` stays
              null until one is pressed so the form cannot be submitted with
              the question quietly unanswered.
            */}
            <fieldset>
              <legend className="mb-1 block text-[12.5px] font-bold text-ink">
                Are you a Lincoln University College student?
              </legend>
              <div className="grid grid-cols-2 gap-2.5">
                {[
                  { value: true, label: 'Yes', hint: 'I study at Lincoln' },
                  { value: false, label: 'No', hint: 'I am visiting' },
                ].map((choice) => {
                  const active = form.isLincolnStudent === choice.value
                  return (
                    <button
                      key={choice.label}
                      type="button"
                      aria-pressed={active}
                      onClick={() => {
                        set('isLincolnStudent', choice.value)
                        /*
                          The school field means different things on the two
                          sides — one of 14 fixed options, or free text — so
                          a value typed under one answer is meaningless under
                          the other and is cleared rather than carried over.
                          The Student ID goes with it when the answer is "No".
                        */
                        set('school', '')
                        if (!choice.value) set('studentId', '')
                      }}
                      className={cn(
                        'rounded-lg border px-3 py-2.5 text-left transition-colors',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/30',
                        active
                          ? 'border-orange-400 bg-orange-50/70'
                          : 'border-slate-200 bg-white hover:bg-slate-50',
                      )}
                    >
                      <span
                        className={cn(
                          'block text-sm font-bold',
                          active ? 'text-orange-700' : 'text-[#0B1E3F]',
                        )}
                      >
                        {choice.label}
                      </span>
                      <span className="block text-[11.5px] text-slate-500">{choice.hint}</span>
                    </button>
                  )
                })}
              </div>
              {errors.isLincolnStudent && (
                <p className="mt-1 text-[11.5px] text-orange">{errors.isLincolnStudent}</p>
              )}
            </fieldset>

            {/*
              Everything below depends on the answer, so it stays hidden until
              there is one. A form that shows "Student ID" and then takes it
              away again reads as a glitch.
            */}
            {form.isLincolnStudent !== null && (
              <>
                <Field label="Full Name" htmlFor="f-name" error={errors.name}>
                  <InputWithIcon
                    icon={User}
                    id="f-name"
                    value={form.name}
                    onChange={(e) => set('name', e.target.value)}
                    autoComplete="name"
                    aria-invalid={!!errors.name}
                    placeholder="Please enter your full name"
                    className={inputClass}
                  />
                </Field>

                {form.isLincolnStudent ? (
                  <Field label="School" htmlFor="f-school" error={errors.school}>
                    <Select
                      id="f-school"
                      icon={GraduationCap}
                      value={form.school}
                      onValueChange={(v) => set('school', v)}
                      options={SCHOOLS}
                      placeholder="Select your school"
                      invalid={!!errors.school}
                    />
                  </Field>
                ) : (
                  <Field
                    label="School / College / University"
                    htmlFor="f-institution"
                    error={errors.school}
                    /*
                      The counter appears only in the last 15 characters. Shown
                      from the first keystroke it is noise on a field most people
                      fill in 25 characters; shown only at the limit it arrives
                      after the student has already been cut off mid-word.
                    */
                    hint={
                      form.school.length > INSTITUTION.maxLength - 15 ? (
                        <span
                          className={cn(
                            'font-semibold tabular-nums',
                            form.school.length >= INSTITUTION.maxLength && 'text-orange',
                          )}
                        >
                          {form.school.length} / {INSTITUTION.maxLength} characters
                          {form.school.length >= INSTITUTION.maxLength && ' — limit reached'}
                        </span>
                      ) : (
                        'The institution you are attending from.'
                      )
                    }
                  >
                    <InputWithIcon
                      icon={GraduationCap}
                      id="f-institution"
                      value={form.school}
                      onChange={(e) => set('school', e.target.value)}
                      autoComplete="organization"
                      /*
                        The browser stops typing here. The same limit is checked
                        in `validate` and again on the server, because `maxLength`
                        governs typing and nothing else — it does not survive a
                        paste on every browser, and it is trivially removed in dev
                        tools.
                      */
                      maxLength={INSTITUTION.maxLength}
                      aria-invalid={!!errors.school}
                      placeholder="e.g. Universiti Malaya"
                      className={inputClass}
                    />
                  </Field>
                )}

                <div className="grid gap-3.5 sm:grid-cols-2">
                  {/*
                Lincoln students only. A visitor has no Lincoln Student ID,
                and an optional box labelled "Student ID" invites them to type
                their own institution's number into a field that is unique
                across this event — which would collide with a real Lincoln ID
                sooner or later.
              */}
                  {form.isLincolnStudent && (
                    <Field label="Student ID" htmlFor="f-sid" error={errors.studentId}>
                      <InputWithIcon
                        icon={IdCard}
                        id="f-sid"
                        value={form.studentId}
                        onChange={(e) => set('studentId', e.target.value.toUpperCase())}
                        aria-invalid={!!errors.studentId}
                        placeholder="LUC-2291034"
                        className={`${inputClass} uppercase`}
                      />
                    </Field>
                  )}

                  <Field label="Phone Number" htmlFor="f-phone" error={errors.phone}>
                    <InputWithIcon
                      icon={Phone}
                      id="f-phone"
                      type="tel"
                      inputMode="tel"
                      value={form.phone}
                      onChange={(e) => set('phone', e.target.value)}
                      autoComplete="tel"
                      aria-invalid={!!errors.phone}
                      placeholder="+60 12-345 6789"
                      className={inputClass}
                    />
                  </Field>
                </div>

                <Field label="Email Address" htmlFor="f-email" error={errors.email}>
                  <InputWithIcon
                    icon={Mail}
                    id="f-email"
                    type="email"
                    inputMode="email"
                    value={form.email}
                    onChange={(e) => set('email', e.target.value)}
                    autoComplete="email"
                    aria-invalid={!!errors.email}
                    placeholder="Your code and certificate will be sent here"
                    className={inputClass}
                  />
                </Field>

                {/*
              PDPA consent — slide 30.

              The notice is a real link, not bold text. This previously said
              "I agree to the PDPA notice" with nothing to read, which is not
              informed consent: section 7 of the Act requires the data subject
              to be *told* what is collected and why before they agree. The
              link opens in a new tab so a half-filled form is not lost.
            */}
                <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-slate-200 bg-white px-3.5 py-3 text-[13px] leading-snug text-slate-600 transition-colors hover:bg-slate-50 has-[:checked]:border-orange-400 has-[:checked]:bg-orange-50/60">
                  <input
                    type="checkbox"
                    checked={form.pdpaConsent}
                    onChange={(e) => set('pdpaConsent', e.target.checked)}
                    className="mt-0.5 size-4 shrink-0 accent-orange-500"
                  />
                  <span>
                    I have read and agree to the{' '}
                    <Link
                      to="/pdpa"
                      target="_blank"
                      rel="noopener"
                      onClick={(e) => e.stopPropagation()}
                      className="font-semibold text-orange-600 underline decoration-orange-300 underline-offset-2 hover:decoration-orange-600"
                    >
                      Personal Data Protection Notice
                    </Link>
                    . My details are used only to run this event, issue my certificate and contact me
                    about it.
                  </span>
                </label>
                {errors.pdpaConsent && (
                  <p className="px-1 text-[11.5px] font-medium text-red-500">{errors.pdpaConsent}</p>
                )}

                {rateLimited && (
                  <Note tone="error" className="rounded-lg border-red-200 bg-red-50/80 text-red-700">
                    {rateLimited} If it keeps happening, the help desk can register you.
                  </Note>
                )}

                {serverError && (
                  <Note tone="error" className="rounded-lg border-red-200 bg-red-50/80 text-red-700">
                    {serverError === 'email_registered' ? (
                      <>
                        That e-mail is already registered.{' '}
                        <Link to="/login" className="font-bold underline">
                          Sign in instead
                        </Link>
                      </>
                    ) : (
                      <>Something went wrong. Please try again, or visit the help desk.</>
                    )}
                  </Note>
                )}

                <Button
                  type="submit"
                  block
                  disabled={submitting}
                  className="h-11 rounded-lg bg-orange-500 py-0 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-orange-600 disabled:opacity-70"
                >
                  {submitting ? 'Sending secure code…' : 'Register & send OTP'}
                </Button>
              </>
            )}
          </form>

          <p className="mt-4 text-center text-sm text-slate-600">
            Already registered?{' '}
            <Link to="/login" className="font-semibold text-orange-600 hover:underline">
              Sign in with your e-mail
            </Link>
          </p>
        </CardContent>
      </Card>
    </AuthLayout>
  )
}
